import { RouteConfig } from 'vue-router';
import MemberRegister from '@views/member/register';
import MemberConfirm from '@views/member/confirm';
import Loading from '@views/loading';
import { LoadingContext } from '@views/loading/context';
import { useLoading as useLiteSettlementLoading } from '@views/loading/liteSettlementLoading';
import { useLoading as useAtoneLiteInitLoading } from '@views/loading/atoneLiteInitLoading';
import AuthSms from '@views/auth/sms';
import LiteLoginAtone from '@views/login_lite/atone';
import LiteLoginGuest from '@views/login_lite/guest';
import LiteLoginNp from '@views/login_lite/np';
import AuthNg from '@views/fail/auth-ng';
import { AuthNgFailContext } from '@views/fail/auth-ng/composable';
import atoneLiteNgFailContext from '@views/fail/auth-ng/AtoneLiteAuthNgContext';
import AppEmptyRoute from '@berlin-front/common/components/elements/AppEmptyRoute';
import atoneMemberRegisterFactoryParam from '@views/member/register/atoneMemberRegisterFactoryParam';
import { HEADER_TYPE } from '@berlin-front/common/components/clusters/ModalHeader/composable';
import { AtoneNPMemberRegisterGroupContext } from '@views/member/AtoneNPMemberRegisterGroupContext';
import { MemberRequestContext } from '@views/member/MemberRequestContext';
import { MemberRegisterContext, useMemberRegisterFactory } from '@views/member/register/composable';
import { ConfirmMemberContext, useMemberConfirmFactory } from '@views/member/confirm/composable';
import { useRegisterSmsAuthWith } from '@views/auth/sms/memberRegisterAuth';
import { useSmsAuth as useLiteNewGuestSmsAuth } from '@views/auth/sms/liteNewGuestAuth';
import { useSmsAuth as useLiteExistGuestSmsAuth } from '@views/auth/sms/liteExistGuestAuth';
import { useSmsAuth as useLiteMemberAuth } from '@views/auth/sms/liteExistMemberAuth';
import { LiteLoginGuestContext } from '@views/login_lite/guest/composable';
import { LiteLoginAtoneContext } from '@views/login_lite/atone/composable';
import { LiteLoginNpContext } from '@views/login_lite/np/composable';
import { SmsAuthContext } from '@views/auth/sms/context';
import OfficialIdentification from '@views/fail/official-identification';
import SpappLogin from '@views/fail/spapp-login';
import { SpappLoginFailContext } from '@views/fail/spapp-login/composable';
import { OfficialIdentificationFailContext } from '@views/fail/official-identification/composable';

import { includes } from '@berlin-front/common/utils';
import { ROUTE_NAME } from './route-name';

const atoneLiteRoute: RouteConfig = {
  path: '/atone-lite',
  // データのライフサイクルが複数の画面に跨る場合は、AppEmptyRouteを使用する。
  // ここで定義したコンテキストでデータの持ち回しを行う。
  components: {
    default: AppEmptyRoute,
    contextProvider: MemberRequestContext.provider,
  },
  children: [
    {
      // atone-lite起動ローディング画面
      path: 'loading',
      name: ROUTE_NAME.ATONE_LITE_LOADING,
      meta: {
        cancelModalType: 'reject',
        headerType: HEADER_TYPE.SHOP_PRICE,
      },
      components: {
        default: Loading,
        contextProvider: LoadingContext.provider,
      },
      props: {
        contextProvider: useAtoneLiteInitLoading,
      },
    },
    {
      // atoneつど決済審査中ローディング画面
      path: 'loading/settlement',
      name: ROUTE_NAME.ATONE_LITE_LOADING_SETTLEMENT,
      meta: {
        cancelModalType: 'reject',
        headerType: HEADER_TYPE.SHOP_PRICE,
      },
      components: {
        default: Loading,
        contextProvider: LoadingContext.provider,
      },
      props: {
        // ローディングコンテキストは差し替えで表示する。
        contextProvider: useLiteSettlementLoading,
      },
    },
    {
      // atoneLite与信NG
      path: 'fail/auth-ng',
      name: ROUTE_NAME.ATONE_LITE_FAIL_AUTH_NG,
      components: {
        default: AuthNg,
        contextProvider: AuthNgFailContext.provider,
      },
      props: {
        contextProvider: atoneLiteNgFailContext,
      },
      meta: {
        cancelModalType: 'shopping',
        headerType: HEADER_TYPE.SHOP_PRICE,
      },
    },
    {
      // アプリ認証画面
      path: 'fail/spapp-login',
      name: ROUTE_NAME.ATONE_LITE_FAIL_SPAPP_LOGIN,
      meta: {
        headerType: HEADER_TYPE.SHOP_PRICE,
      },
      components: {
        default: SpappLogin,
        contextProvider: SpappLoginFailContext.provider,
      },
    },
    {
      // 公的証明証
      path: 'fail/official-identification',
      name: ROUTE_NAME.ATONE_LITE_FAIL_OFFICIAL_IDENTIFICATION,
      meta: {
        headerType: HEADER_TYPE.SHOP_PRICE,
      },
      components: {
        default: OfficialIdentification,
        contextProvider: OfficialIdentificationFailContext.provider,
      },
    },
    {
      // XXX: ↓新規ってルートは別にあるからor新規って記述は間違い？
      // つど払い/ログイン画面/仮会員 or 新規
      path: 'login/guest',
      name: ROUTE_NAME.ATONE_LITE_LOGIN_GUEST,
      meta: {
        cancelModalType: 'shopping',
        headerType: HEADER_TYPE.SHOP_PRICE,
      },
      components: {
        default: LiteLoginGuest,
        contextProvider: LiteLoginGuestContext.provider,
      },
    },
    {
      // つど払い/ログイン画面/atone会員
      path: 'login/atone',
      name: ROUTE_NAME.ATONE_LITE_LOGIN_ATONE,
      meta: {
        cancelModalType: 'shopping',
        headerType: HEADER_TYPE.SHOP_PRICE,
      },
      components: {
        default: LiteLoginAtone,
        contextProvider: LiteLoginAtoneContext.provider,
      },
    },
    {
      // 新規仮会員SMS認証画面
      path: 'auth/sms/new',
      name: ROUTE_NAME.ATONE_LITE_AUTH_SMS_NEW,
      meta: {
        cancelModalType: 'shopping',
        headerType: HEADER_TYPE.SHOP_PRICE,
      },
      components: {
        default: AuthSms,
        contextProvider: SmsAuthContext.provider,
      },
      props: {
        contextProvider: useLiteNewGuestSmsAuth,
        default: (route) => {
          const { previous } = route.query;
          return typeof previous === 'string' && includes(...Object.values(ROUTE_NAME))(previous) ? { previous } : {};
        },
      },
    },
    {
      // 既存仮会員SMS認証画面
      path: 'auth/sms/exist',
      name: ROUTE_NAME.ATONE_LITE_AUTH_SMS_GUEST,
      meta: {
        cancelModalType: 'shopping',
        headerType: HEADER_TYPE.SHOP_PRICE,
      },
      components: {
        default: AuthSms,
        contextProvider: SmsAuthContext.provider,
      },
      props: {
        contextProvider: useLiteExistGuestSmsAuth,
        default: (route) => {
          const { previous } = route.query;
          return typeof previous === 'string' && includes(...Object.values(ROUTE_NAME))(previous) ? { previous } : {};
        },
      },
    },
    {
      // 会員SMS認証画面
      path: 'sms-auth',
      name: ROUTE_NAME.ATONE_LITE_AUTH_SMS_ATONE,
      meta: {
        cancelModalType: 'shopping',
        headerType: HEADER_TYPE.SHOP_PRICE,
      },
      components: {
        default: AuthSms,
        contextProvider: SmsAuthContext.provider,
      },
      props: {
        contextProvider: useLiteMemberAuth,
        default: (route) => {
          const { previous } = route.query;
          return typeof previous === 'string' && includes(...Object.values(ROUTE_NAME))(previous) ? { previous } : {};
        },
      },
    },
    {
      path: 'np-member',
      components: {
        default: AppEmptyRoute,
        contextProvider: AtoneNPMemberRegisterGroupContext.provider,
      },
      children: [
        {
          // つど払い/NP会員ログイン
          path: 'login',
          name: ROUTE_NAME.ATONE_LITE_MEMBER_NP_LOGIN,
          meta: {
            cancelModalType: 'shopping',
            headerType: HEADER_TYPE.SHOP_PRICE,
          },
          components: {
            default: LiteLoginNp,
            contextProvider: LiteLoginNpContext.provider,
          },
        },
        {
          // 会員登録画面
          path: 'register',
          name: ROUTE_NAME.ATONE_LITE_MEMBER_REGISTER,
          meta: {
            cancelModalType: 'shopping',
            headerTitle: 'NP会員IDをatoneに連携',
            headerType: HEADER_TYPE.TITLE_ONLY,
          },
          components: {
            default: MemberRegister,
            contextProvider: MemberRegisterContext.provider,
          },
          props: {
            contextProvider: {
              useMemberRegister: useMemberRegisterFactory(
                atoneMemberRegisterFactoryParam({
                  nextPage: ROUTE_NAME.ATONE_LITE_MEMBER_CONFIRM,
                  prevPage: ROUTE_NAME.ATONE_LITE_MEMBER_NP_LOGIN,
                }),
              ),
            },
          },
        },
        {
          // 会員登録確認画面
          path: 'confirm',
          name: ROUTE_NAME.ATONE_LITE_MEMBER_CONFIRM,
          meta: {
            cancelModalType: 'shopping',
            headerType: HEADER_TYPE.TITLE_ONLY,
            headerTitle: '会員情報の確認',
          },
          components: {
            default: MemberConfirm,
            contextProvider: ConfirmMemberContext.provider,
          },
          props: {
            contextProvider: {
              useMemberConfirm: useMemberConfirmFactory({
                nextPage: ROUTE_NAME.ATONE_LITE_MEMBER_AUTH,
                prevPage: ROUTE_NAME.ATONE_LITE_MEMBER_REGISTER,
              }),
            },
          },
        },
        {
          // 既存NP会員SMS認証画面
          path: 'auth',
          name: ROUTE_NAME.ATONE_LITE_MEMBER_AUTH,
          meta: {
            cancelModalType: 'shopping',
            headerType: HEADER_TYPE.SHOP_PRICE,
          },
          components: {
            default: AuthSms,
            contextProvider: SmsAuthContext.provider,
          },
          props: {
            contextProvider: useRegisterSmsAuthWith({
              registerPage: ROUTE_NAME.ATONE_LITE_MEMBER_REGISTER,
              nextPage: ROUTE_NAME.ATONE_LITE_LOADING_SETTLEMENT,
            }),
          },
        },
      ],
    },
  ],
};

export default atoneLiteRoute;
